import { FC } from 'react';
import { Badge, Box, BoxProps, Tab, Tabs, TabsProps, Tooltip } from '@mui/material';
import { AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon } from '@mui/icons-material';

import AccessGuard from '../../../../../components/AccessGuard';
import { SectionHeading } from '../../../../../components/SectionHeading';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';
import Printable from '../../../../../components/Printable';
import Row from '../../../../../components/Row';
import { USER_GROUP_ADMIN, USER_GROUP_AGENCY } from '../../../../../types/User';

import { useCampaignPageContext } from '../../../../../hooks/Contexts/Pages/CampaignPageContext';

const AdminOnlyIcon = () => {
    return <AdminPanelSettingsOutlinedIcon fontSize="small" color="primary" sx={{ marginLeft: '5px' }} />;
};

const LOON_AGENCY_ID = 1;

const PAGE_TABS = {
    overview: { label: 'Overview', slug: 'overview' },
    flights: { label: 'Flights', slug: 'flights' },
    'ad-groups': { label: 'Ad Groups', slug: 'ad-groups' },
    creatives: { label: 'Creatives', slug: 'creatives' },
    'change-history': { label: 'Change History', slug: 'change-history' },
    performance: { label: 'Performance', slug: 'performance' },
} as const;

type TabSlug = keyof typeof PAGE_TABS;

function isTabSlug(value: string): value is TabSlug {
    return value in PAGE_TABS;
}

type CampaignPageTabsProps = {
    slotProps?: {
        box?: BoxProps;
    };
} & TabsProps;

const CampaignPageTabs: FC<CampaignPageTabsProps> = ({ slotProps = {}, ...props }): React.JSX.Element => {
    const { userContext } = useUserContext();
    const { campaign } = useCampaignPageContext();
    const tab: string = props.value as string;

    return (
        <Box
            sx={{
                mt: (theme) => theme.spacing(2),
            }}
            {...slotProps?.box}
        >
            <Printable show={false}>
                <Tabs {...props}>
                    <Tab value={PAGE_TABS.overview.slug} label="Campaign" />
                    <Tab value={PAGE_TABS.flights.slug} label="Flights" />
                    {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                        <Tab
                            value={PAGE_TABS['ad-groups'].slug}
                            label={
                                <Tooltip title="Visible to Admins Only" placement="top" enterNextDelay={500}>
                                    <Row gap={0.5} alignItems="center">
                                        Ad Groups
                                        <AdminOnlyIcon />
                                    </Row>
                                </Tooltip>
                            }
                        />
                    )}
                    <Tab value={PAGE_TABS.creatives.slug} label="Creatives" />
                    <Tab
                        value={PAGE_TABS['change-history'].slug}
                        label={
                            <Row>
                                Change History
                                {campaign?.hasUpdates && (
                                    <Badge
                                        color="error"
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                            },
                                        }}
                                        badgeContent={1}
                                    />
                                )}
                            </Row>
                        }
                    />
                    {(userContext.isAdmin() || userContext.agencyId === LOON_AGENCY_ID) && (
                        <Tab
                            value={PAGE_TABS.performance.slug}
                            label={
                                <Tooltip title="Visible to Admins & Loon Only" placement="top" enterNextDelay={500}>
                                    <Row gap={0.5} alignItems="center">
                                        Performance
                                        <AdminOnlyIcon />
                                    </Row>
                                </Tooltip>
                            }
                        />
                    )}
                </Tabs>
            </Printable>
            <Printable show={true}>
                <Box sx={{ mb: 2 }}>
                    {tab === PAGE_TABS.overview.slug && <SectionHeading divider={false}>Campaign</SectionHeading>}
                    {tab === PAGE_TABS['ad-groups'].slug && (
                        <AccessGuard
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                            condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                        >
                            <SectionHeading divider={false}>Ad Groups</SectionHeading>
                        </AccessGuard>
                    )}
                    {tab === PAGE_TABS.creatives.slug && <SectionHeading divider={false}>Creatives</SectionHeading>}
                    {tab === PAGE_TABS['change-history'].slug && (
                        <SectionHeading divider={false}>Change History</SectionHeading>
                    )}
                </Box>
            </Printable>
        </Box>
    );
};

export type { CampaignPageTabsProps, TabSlug };
export { PAGE_TABS, isTabSlug };
export default CampaignPageTabs;
